import { Link, Outlet } from "react-router-dom";
import { SiteNav, SiteFooter } from "@comai-portal/design-system";
import type { ReactNode } from "react";
import { VENDOR_PLATFORMS, CUSTOMER_PLATFORMS } from "../content/platforms";
import type { Platform } from "../content/platforms";
import { useThemeMode } from "../theme/useThemeMode";
import { Brand } from "./Brand";

/**
 * App shell — now on the shared design system. (CO 069)
 * Nav structure (logical-knife pass): Platforms ▾ · For agents · Pricing ·
 * Coms · Trust · Docs, then Sign in / Start free. Blog moved to the footer.
 * Coms joined the header with the CO 315 shelf-life disclosure — the currency's
 * not-a-store-of-value quality is a headline feature, not a footer footnote.
 */

// CO 613 (Ron 2026-10-11): Prompts is a header destination — the library was
// reachable only from the footer. The slot comes from Honesty, which folds into
// Trust: /trust renders the live ratio itself, and /honesty stays a served page
// linked from the footer.
const NAV_ITEMS = [
  { label: "Prompts", to: "/prompts" },
  { label: "For agents", to: "/agents" },
  { label: "Pricing", to: "/pricing" },
  { label: "Coms", to: "/coms" },
  { label: "Trust", to: "/trust" },
  { label: "Try it live", to: "/demo" },
  { label: "Docs", to: "/docs" },
  { label: "Start free", to: "/start" },
];

// A business owner doesn't think in "platforms" — they think "how I make money"
// and "how I spend it." So the nav is split into those two buckets, not one
// catch-all Platforms menu. Vendor = the selling face (money in); customer = the
// tools you spend on to run it (money out). (CO 032-004, Ron 2026-06-13)
const toItem = (p: Platform) => ({
  label:
    p.status === "coming-soon"
      ? `${p.name} (soon)`
      : p.status === "preset"
        ? `${p.name} (preset)`
        : p.name,
  to: `/platforms/${p.slug}`,
  sub: p.replaces ? `Replaces ${p.replaces.slice(0, 2).join(", ")}` : p.tagline,
});

const PLATFORM_DROPDOWNS = [
  {
    label: "Make money",
    items: [
      {
        label: "What you can sell on",
        to: "/platforms",
        sub: "All the ways to earn",
      },
      ...VENDOR_PLATFORMS.map(toItem),
    ],
  },
  {
    label: "Run & spend",
    items: [
      {
        label: "Tools to run it",
        to: "/platforms",
        sub: "What you compose in",
      },
      ...CUSTOMER_PLATFORMS.map(toItem),
    ],
  },
];

const FOOTER_COLUMNS = [
  {
    title: "Product",
    links: [
      { label: "Install a node", to: "/install" },
      { label: "For operators", to: "/operators" },
      { label: "For agents", to: "/agents" },
      { label: "Platforms", to: "/platforms" },
      { label: "Coms", to: "/coms" },
      { label: "Pricing", to: "/pricing" },
    ],
  },
  {
    title: "Trust & proof",
    links: [
      { label: "Trust", to: "/trust" },
      { label: "Honesty ratio", to: "/honesty" },
      { label: "Security", to: "/security" },
      { label: "Durability", to: "/durability" },
      { label: "Try it live", to: "/demo" },
      {
        // CO 237: now an internal route on this site, not an external link.
        label: "Agent Readiness Check",
        to: "/agent-ready",
      },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Docs", to: "/docs" },
      { label: "Blog", to: "/blog" },
      // CO 530 — the prompt library, generated from canon.
      { label: "Prompt library", to: "/prompts" },
      // CO 680 Phase 2b — the theme library, generated from the house supply
      // with its standing read live.
      { label: "Theme library", to: "/themes" },
      { label: "Support", to: "/support" },
      // CO 321 — agent-native front door: "Start free" points at the /start
      // action page (become-a-manager prompt); "Sign in" removed (no human session).
      { label: "Start free", to: "/start" },
    ],
  },
];

const LEGAL_LINKS = [
  { label: "Privacy", to: "/privacy" },
  { label: "Terms", to: "/terms" },
  { label: "Acceptable Use", to: "/acceptable-use" },
  { label: "Cookies", to: "/cookies" },
  { label: "DPA", to: "/dpa" },
];

function renderNavLink({
  to,
  className,
  children,
  onClick,
}: {
  to: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <Link to={to} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

function renderFooterLink({
  to,
  external,
  children,
}: {
  to: string;
  external?: boolean;
  children: ReactNode;
}) {
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return <Link to={to}>{children}</Link>;
}

export function Layout() {
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === "dark";

  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink dark:bg-ink dark:text-paper">
      <SiteNav
        renderLink={renderNavLink}
        brand={<Brand />}
        dropdowns={PLATFORM_DROPDOWNS}
        items={NAV_ITEMS}
        isDark={isDark}
        onToggleTheme={toggleMode}
        ctaTo="/install"
        ctaLabel="Install ComOS"
      />
      <main className="flex-1">
        <Outlet />
      </main>
      <SiteFooter
        renderLink={renderFooterLink}
        brand={<Brand />}
        tagline="The operating system you run your business on. Set it up free; pay only when money moves."
        columns={FOOTER_COLUMNS}
        legal={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
              © {new Date().getFullYear()} ComOS · Your business, any platform.
            </span>
            <span className="flex flex-wrap gap-x-4 gap-y-1">
              {LEGAL_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="hover:text-signal dark:hover:text-signal-bright transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </span>
          </div>
        }
      />
    </div>
  );
}
